import React, { useCallback, useEffect, useState } from "react"
// import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify"
import { useNavigate, useParams } from "react-router-dom"
import { request } from "../../services/request"
import { BillDetail, BillDetails, PaySetting, PaySettings } from "../../services/authinterface"
import Table, { TableAction } from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import moment from "moment"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import BillModel from "../../components/common/BillModel"
import { FaChevronLeft } from "react-icons/fa"
import { donwloadCSV, downloadPDF } from "../../utils/exportFunctions"
import PrimaryButton from "../../components/subComponents/PrimaryButton"

const Icon: any = FaChevronLeft

const CustomerBill: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [paySetting, setPaySetting] = React.useState<PaySetting | null>(null)
  const [bills, setBills] = useState<BillDetail[]>([])
  const [customerDetail, setCustomerDetail] = useState<any>(null)
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalItems, setTotalItems] = useState(100)
  const [search, setSearch] = useState("")
  const debounceText = useDebounce(search, 500)
  const [sort, setSort] = useState("DESC")
  const [sortCol, setSortCol] = useState("createdAt")
  const [billModel, setBillModel] = useState<boolean>(false)
  const navigate = useNavigate()
  const param = useParams()

  const fetchPaySettingDetails = React.useCallback(async () => {
    try {

      const response = await request<PaySettings>({
        url: `/adminPaySetting/getPaySettingDetails`,
        method: "GET",
      })

      if (response.success) {
        setPaySetting(response.data)
      } else {
        toast.error("Failed to fetch paySetting: " + response.message)
      }
    } catch (error) {
    }
  }, [])

  React.useEffect(() => {
    fetchPaySettingDetails()
  }, [fetchPaySettingDetails])

  const fetchCustomerBills = useCallback(async () => {
    try {
      dispatch(changeloaderState(true))

      const params = {
        page: page,
        limit: rowsPerPage,
        search: debounceText,
        sort: sort,
        sortCol: sortCol,
      }
      const response = await request<BillDetails>({
        url: `/adminCustomers/getBills/${param.id}`,
        method: "POST",
        data: params,
      })

      if (response.success) {
        setBills(response.data)
        setCustomerDetail(response.otherData)
        setTotalItems(response.total)
      } else {
        toast.error("Failed to fetch customers: " + response.message)
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    } finally {
      dispatch(changeloaderState(false))
    }
  }, [page, rowsPerPage, debounceText, sort, sortCol, dispatch, param.id])

  useEffect(() => {
    fetchCustomerBills()
  }, [page, rowsPerPage, debounceText, sort, fetchCustomerBills])

  useEffect(() => {
    if (debounceText) {
      setPage(1) // Reset to first page on search
    }
  }, [debounceText])

  const fetchAllBills = async () => {
    try {
      const params = {
        page: 1,
        search: debounceText,
        limit: 100000,
        sort: sort,
        sortCol: sortCol,
      }

      const response = await request<BillDetails>({
        url: `/adminCustomers/getBills/${param.id}`,
        method: "POST",
        data: params,
      })

      if (response.success) {
        return response.data
      } else {
        toast.error("Failed to fetch bills: " + response.message)
        return []
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
      return []
    }
  }


  const exportCSV = async () => {

    const allData = await fetchAllBills()

    if (!allData.length) {
      toast.error("No data available to export")
      return
    }

    const headers = [
      "Sr. No.",
      "Product Count",
      "Bill Date",
      "Bill Type",
      "Bill Amount",
      "Status",
    ]

    const rows = allData.map((bill, index) => [
      index + 1,
      bill.totalInstalled,
      moment(bill.billDate).format("MMM-DD-YYYY"),
      bill.type,
      `${bill.billAmount} usd`,
      bill.status === "0"
        ? "Pending"
        : bill.status === "1"
          ? "Paid"
          : bill.status === "9"
            ? "Payment Failed"
            : "In Progress",
    ])

    donwloadCSV(headers, rows, "Bills.csv")
  }

  const exportPDF = async () => {

    const allData = await fetchAllBills()

    if (!allData.length) {
      toast.error("No data available to export")
      return
    }

    const headers = [
      "Sr. No.",
      "Product Count",
      "Bill Date",
      "Bill Type",
      "Bill Amount",
      "Status",
    ]

    const rows = allData.map((bill, index) => [
      index + 1,
      bill.totalInstalled,
      moment(bill.billDate).format("MMM-DD-YYYY"),
      bill.type,
      `${bill.billAmount} usd`,
      bill.status === "0"
        ? "Pending"
        : bill.status === "1"
          ? "Paid"
          : bill.status === "9"
            ? "Payment Failed"
            : "In Progress",
    ])

    // call utility
    downloadPDF(headers, rows, "Bills")
  }

  const columns = [
    {
      name: "Product Count",
      value: (customer: BillDetail) => customer.totalInstalled,
      id: "totalInstalled",
    },
    {
      name: "Bill date",
      value: (customer: BillDetail) => moment(customer.billDate).format("MMM-DD-YYYY"),
      id: "billDate",
    },
    {
      name: "Product",
      value: (customer: BillDetail) => customer.type,
      id: "type",
    },
    {
      name: "Bill Type",
      value: (customer: BillDetail) => customer.billType,
      id: "type",
    },
    {
      name: "Current Bill Amount",
      value: (customer: BillDetail) => customer.billAmount + " usd",
      id: "type",
    },
    {
      name: "Status",
      value: (customer: BillDetail) =>
        customer.status === "0"
          ? "Pending"
          : customer.status === "1"
            ? "Paid"
            : customer.status === "9"
              ? "Payment Failed"
              : "In Progress",
      id: "status",
    },
    {
      name: "Next Billing",
      value: (customer: BillDetail) => customer?.nextBillDate ? moment(customer?.nextBillDate).format("MMM-DD-YYYY") : "-",
      id: "billDate",
    },
    {
      name: "Next Bill Amount",
      value: (customer: BillDetail) => customer?.billType === "Subscription" ? (
        customer?.type === "SOS" ?
          (paySetting?.status === "1" ?
            Number(paySetting.SOS_bill_amount) * Number(customer.totalInstalled)
            :
            Number(paySetting?.test_SOS_bill_amount) * Number(customer.totalInstalled)
          )
          : // This is the condition for when customer?.type is NOT "SOS"
          (paySetting?.status === "1" ?
            Number(paySetting.Sensor_bill_amount) * Number(customer.totalInstalled)
            :
            Number(paySetting?.test_Sensor_bill_amount) * Number(customer.totalInstalled)
          )
      ) + " usd" : "-",
      id: "type",
    },
  ]
  const action: TableAction[] = [
    {
      label: "Invoice",
      // onClick: (row: BillDetail) => {
      //   navigate(`/customer/invoice/${row.id}`)
      // },
      isRedirect: true,
      link: (row: BillDetail) => `/customer/invoice/${row.id}`,
      className: "table-btn-1",
    },
  ]

  const addAction = {
    label: "Generate One-time Charge",
    type: "button",
    onClick: () => {
      setBillModel(true)
    },
    className: "resetBtn",
  }

  const getBack = () => {
    navigate(-1)
  }

  return (
    <section className=' dashboard-section dashboard-item '>
      <BillModel
        show={billModel}
        onClose={() => setBillModel(false)}
        fetchCustomerBills={fetchCustomerBills}
      />
      <ToastContainer position='top-right' autoClose={2000} theme='light' />
      <div className='container'>
        <div className='row'>
          <div className='col-12'>
            <div className='d-flex gap-1 mb-2'>
              <button onClick={getBack} className='d-flex align-items-center'>
                <Icon size={20} />
              </button>
              <div className='dashboard-table-title-section w-100'>
                <h2>Bills</h2>
                <div className='dashboard-table-title-right-side d-flex gap-2'>
                  <PrimaryButton
                    label='Export CSV'
                    className='max-w-max-content'
                    onClick={exportCSV}
                  />
                  <PrimaryButton
                    label='Export PDF'
                    className='max-w-max-content'
                    onClick={exportPDF}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <div className='dashboard-table-text-section'>
              {/* <h2>Owner Details : </h2> */}
              <p className=''>
                Name :{" "}
                {/* {`${bills[0]?.usersBillings?.first_name} ${bills[0]?.usersBillings?.last_name} `} */}
                {(customerDetail?.first_name || customerDetail?.last_name) &&
                  `${customerDetail?.first_name || ""} ${customerDetail?.last_name || ""}`}
              </p>
              <p className=''>Billing Information : {customerDetail?.paymentUpdated === 1 ? "Yes" : "No"}</p>
              <p className=''>Email : {customerDetail?.email}</p>
            </div>
            <div className='dashboard-table-text-section'>
              {/* {bills[0]?.buttons_purchased && */}
              <p className=''>
                Buttons Purchased :{" "}
                {/* {`${bills[0]?.usersBillings?.first_name} ${bills[0]?.usersBillings?.last_name} `} */}
                {bills[0]?.buttons_purchased || "0"}
              </p>
              {/* } */}
              {/* {bills[0]?.buttons_provisioned &&  */}
              <p className=''>
                Buttons Provisioned :{" "}
                {/* {`${bills[0]?.usersBillings?.first_name} ${bills[0]?.usersBillings?.last_name} `} */}
                {bills[0]?.buttons_provisioned || "0"}
              </p>
              {/* } */}
              {/* {bills[0]?.sensors_purchased &&  */}
              <p className=''>
                Sensors Purchased :{" "}
                {/* {`${bills[0]?.usersBillings?.first_name} ${bills[0]?.usersBillings?.last_name} `} */}
                {bills[0]?.sensors_purchased || "0"}
              </p>
              {/* } */}
              {/* {bills[0]?.sensors_provisioned &&  */}
              <p className=''>
                Sensors Provisioned :{" "}
                {/* {`${bills[0]?.usersBillings?.first_name} ${bills[0]?.usersBillings?.last_name} `} */}
                {bills[0]?.sensors_provisioned || "0"}
              </p>
              {/* } */}
            </div>
            <div className='dashboard-table-text-section'></div>
          </div>

          <div className='col-12 mt-4'>
            <div className='table-container-main'>
              <div className='table-details-section'></div>
              <Table
                rowData={bills}
                columns={columns}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalItems={totalItems}
                setSortColumn={setSortCol}
                setSortOrder={setSort}
                sortColumn={sortCol}
                sortOrder={sort}
                action={action}
                isSearch
                search={search}
                setSearch={setSearch}
                addAction={addAction}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CustomerBill
